import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MonitorUp, X, Loader2, MousePointer2, ShieldCheck } from 'lucide-react';
import { useAuth, api } from '../../context/AuthContext';
import { KANZLEI_PFAD } from '../kanzlei/KanzleiLeiste';
import { usePortalDockSichtbar } from '../portal/PortalDock';
import { ZUSTIMMUNG } from '../../lib/cobrowse';
import { useCobrowseSitzung, useEinladung } from './useCobrowseSitzung';
import { NetzPunkt } from './Netzanzeige';

const TEAM = ['admin', 'kanzlei_mitarbeiter', 'freelancer'];

const Zustimmung = ({ titel, text, okText, onOk, onAbbruch }) => {
  const [ja, setJa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const los = async () => { setBusy(true); setFehler(''); try { await onOk(); } catch (e) { setFehler(e?.response?.data?.detail || 'Start fehlgeschlagen'); } finally { setBusy(false); } };
  return (
    <div className="rr-block fixed inset-0 z-[9998] bg-[#0B1724]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="cobrowse-zustimmung">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 border border-[#b8893d]/30">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#b8893d]"><MonitorUp className="w-4 h-4" /> Bildschirm teilen</div>
          <button type="button" onClick={onAbbruch} className="text-gray-400 hover:text-[#1B3A5C]" aria-label="Schließen" data-testid="cobrowse-zustimmung-schliessen"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-serif text-2xl text-[#1B3A5C] mt-2">{titel}</h3>
        <p className="text-sm text-[#34536b] mt-2">{text}</p>
        <label className="flex items-start gap-2.5 mt-4 p-3 rounded-xl bg-[#f5f8fa] text-xs text-[#34536b] cursor-pointer">
          <input type="checkbox" checked={ja} onChange={(e) => setJa(e.target.checked)} className="mt-0.5 accent-[#1B3A5C]" data-testid="cobrowse-zustimmung-check" />
          <span>{ZUSTIMMUNG}</span>
        </label>
        {fehler && <p className="text-xs text-rose-700 mt-2" data-testid="cobrowse-fehler">{fehler}</p>}
        <div className="flex gap-2 mt-5">
          <button type="button" disabled={!ja || busy} onClick={los} className="flex-1 h-11 rounded-[10px] bg-[#1B3A5C] text-white text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="cobrowse-zustimmung-ok">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{okText}
          </button>
          <button type="button" onClick={onAbbruch} className="h-11 px-4 rounded-[10px] border border-gray-300 text-sm text-[#1B3A5C]" data-testid="cobrowse-zustimmung-abbrechen">Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

const Leiste = ({ sitzung, agent, netz, unten, onEnde }) => (
  <div className={`rr-block fixed left-4 ${unten} z-[9997] max-w-[calc(100vw-2rem)] bg-[#0B1724]/95 backdrop-blur text-white rounded-2xl border border-[#b8893d]/50 shadow-2xl pl-4 pr-2 py-2 flex items-center gap-3 text-xs`} data-testid="cobrowse-leiste">
    <span className={`w-2 h-2 shrink-0 rounded-full ${agent ? 'bg-emerald-400 animate-pulse' : 'bg-[#e0b56a]'}`} />
    {agent
      ? <><span className="min-w-0" data-testid="cobrowse-status-aktiv"><b>{agent}</b> sieht und bedient Ihren Bildschirm</span><NetzPunkt netz={netz} /></>
      : sitzung.code
        ? <span className="flex items-center gap-2 min-w-0" data-testid="cobrowse-status-wartet"><span className="hidden sm:inline">Nennen Sie der Kanzlei diesen Code:</span><span className="sm:hidden">Ihr Code:</span>
            <b className="shrink-0 font-mono text-lg tracking-[0.25em] text-[#e0b56a] whitespace-nowrap" data-testid="cobrowse-code">{sitzung.code}</b></span>
        : <span data-testid="cobrowse-status-wartet">Verbindung zur Kanzlei wird aufgebaut …</span>}
    <button type="button" onClick={onEnde} className="shrink-0 h-8 px-3 rounded-full bg-rose-600 hover:bg-rose-700 transition-colors" data-testid="cobrowse-beenden">Beenden</button>
  </div>
);

const Zeiger = ({ maus }) => (
  <div className="rr-block fixed z-[9999] pointer-events-none transition-[left,top] duration-75" style={{ left: maus.x, top: maus.y }} data-testid="cobrowse-zeiger">
    <MousePointer2 className="w-6 h-6 text-[#b8893d] fill-[#e0b56a] drop-shadow" />
    <span className="ml-4 -mt-1 inline-block px-1.5 py-0.5 rounded bg-[#1B3A5C] text-white text-[10px]">Kanzlei</span>
  </div>
);

export const CobrowseClient = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const dock = usePortalDockSichtbar();
  const [offen, setOffen] = useState(false);
  const team = TEAM.includes(user?.role);
  const s = useCobrowseSitzung(pathname);
  const [einladung, setEinladung] = useEinladung(user?.role === 'user' && !s.sitzung);
  if (team) return null;
  const knopf = !s.sitzung && !KANZLEI_PFAD.test(pathname) && !/^\/(login|register|anmelden|passwort|signatur)/.test(pathname);
  return (
    <>
      {knopf && (
        <button type="button" onClick={() => setOffen(true)} className={`rr-block fixed left-4 ${dock ? 'bottom-28' : 'bottom-6'} z-40 inline-flex items-center gap-2 h-10 pl-3 pr-4 rounded-full bg-white/90 backdrop-blur border border-[#1B3A5C]/15 shadow-lg text-xs text-[#1B3A5C] hover:border-[#b8893d] hover:-translate-y-0.5 transition-[transform,border-color] duration-200`}
          data-testid="cobrowse-start" title="Bildschirm mit der Kanzlei teilen">
          <MonitorUp className="w-4 h-4 text-[#b8893d]" /> <span className="hidden sm:inline">Bildschirm teilen</span>
        </button>
      )}
      {s.hinweis && !s.sitzung && (
        <div className="rr-block fixed left-4 bottom-20 z-40 max-w-xs bg-[#0B1724] text-white text-xs rounded-xl px-4 py-3 shadow-xl flex gap-2" data-testid="cobrowse-hinweis">
          <ShieldCheck className="w-4 h-4 text-[#e0b56a] shrink-0" /> <span>{s.hinweis}</span>
          <button type="button" onClick={() => s.setHinweis('')} className="ml-1 text-white/60 hover:text-white" aria-label="Schließen"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {offen && !s.sitzung && (
        <Zustimmung titel="Gemeinsam auf Ihren Bildschirm schauen" okText="Code erzeugen"
          text="Sie erhalten einen 6-stelligen Code. Nennen Sie ihn am Telefon Ihrer Ansprechperson in der Kanzlei. Erst dann sieht die Kanzlei diesen Tab."
          onOk={async () => { await s.starten(); setOffen(false); }} onAbbruch={() => setOffen(false)} />
      )}
      {einladung && !s.sitzung && (
        <Zustimmung titel={`${einladung.agent_name || 'Die Kanzlei'} möchte Ihren Bildschirm sehen`} okText="Annehmen"
          text="Die Kanzlei hat Sie zu einer gemeinsamen Bildschirmsitzung eingeladen, zum Beispiel um Ihnen beim Hochladen oder Ausfüllen zu helfen."
          onOk={async () => { await s.annehmen(einladung.id); setEinladung(null); }}
          onAbbruch={() => { api.post(`/cobrowse/einladung/${einladung.id}/ablehnen`).catch(() => {}); setEinladung(null); }} />
      )}
      {s.sitzung && <Leiste sitzung={s.sitzung} agent={s.agent} netz={s.netz} unten={dock ? 'bottom-28' : 'bottom-6'} onEnde={s.beenden} />}
      {s.maus && s.agent && <Zeiger maus={s.maus} />}
    </>
  );
};

export default CobrowseClient;
